/**
 * Lead Capture Popup Component
 * Shows on first visit to homepage after scrolling
 * Offers free ebook "What's Your Next Best Move?" in exchange for email
 * Then directs to Collective Application
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  X, 
  BookOpen, 
  Sparkles, 
  ArrowRight, 
  CheckCircle2,
  Loader2,
  Download,
  Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EBOOK_URL = "https://customer-assets.emergentagent.com/job_a70f5a78-e294-401f-afc9-fb96dd96ec27/artifacts/9wweeaxp_NEW%20What%27s%20Your%20Next%20Best%20Move%20E-Book%20V.3%20%281%29.pdf";
const STORAGE_KEY = 'sd_lead_popup_shown';

const LeadCapturePopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = form, 2 = success/download
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    // Check if popup was already shown
    const wasShown = localStorage.getItem(STORAGE_KEY);
    if (wasShown) return;

    // Set up scroll listener
    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      
      // Show popup after scrolling 10% of the page
      if (scrollPercent >= 10) {
        setIsOpen(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };

    // Add scroll listener after a short delay to allow page to load
    const timer = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
    }, 2000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    // Mark as shown so it doesn't appear again
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.email) {
      toast.error('Please enter your name and email');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit lead to backend
      await axios.post(`${API_URL}/api/leads/ebook-download`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        source: 'homepage_popup',
        lead_magnet: 'whats_your_next_best_move_ebook'
      });

      // Move to success step
      setStep(2);
      
      // Mark popup as shown
      localStorage.setItem(STORAGE_KEY, 'true');
      
    } catch (error) {
      console.error('Error submitting lead:', error);
      // Still show success even if backend fails - don't block the download
      setStep(2);
      localStorage.setItem(STORAGE_KEY, 'true');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = () => {
    // Create a temporary link to download without navigating away
    const link = document.createElement('a');
    link.href = EBOOK_URL;
    link.download = 'Whats-Your-Next-Best-Move-Ebook.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleApplyToCollective = () => {
    handleClose();
    window.location.href = '/collective-application';
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden border-0 bg-gradient-to-br from-white to-purple-50">
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute right-4 top-4 z-10 p-1 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-gray-400" />
        </button>

        {step === 1 ? (
          /* Step 1: Lead Capture Form */
          <div className="p-6 sm:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 mb-4">
                <BookOpen className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Unlock Your Next Best Move
              </h2>
              <p className="text-gray-600">
                Get our <span className="font-semibold text-purple-700">FREE ebook</span> by Crystal Khalil & Dr. Nicole LaBeach
              </p>
            </div>

            {/* Value Props */}
            <div className="bg-purple-50 rounded-xl p-4 mb-6">
              <p className="text-sm font-medium text-purple-900 mb-3">Inside you'll discover:</p>
              <ul className="space-y-2">
                {[
                  'How to overcome limiting beliefs holding you back',
                  'Strategies for cultivating a growth mindset',
                  'Steps to take empowered action toward your goals',
                  'Tools for building unshakeable resilience'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-purple-800">
                    <CheckCircle2 className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="firstName" className="text-xs text-gray-600">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Crystal"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-xs text-gray-600">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Khalil"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email" className="text-xs text-gray-600">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-xs text-gray-600">Phone (optional - for exclusive updates)</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 000-0000"
                  className="mt-1"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg font-semibold"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Get My Free Ebook
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </form>
          </div>
        ) : (
          /* Step 2: Success / Download */
          <div className="p-6 sm:p-8 text-center">
            {/* Success Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 mb-6">
              <Heart className="h-10 w-10 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              You're In, Beautiful!
            </h2>
            <p className="text-gray-600 mb-4">
              Your ebook is downloading now. We've also sent it to your email.
            </p>

            {/* Download Button - Secondary */}
            <Button 
              onClick={handleDownload}
              variant="outline"
              className="w-full border-2 border-purple-300 text-purple-700 hover:bg-purple-50 py-5 text-base font-semibold mb-6"
            >
              <Download className="h-5 w-5 mr-2" />
              Download Ebook Again
            </Button>

            {/* CTA to Collective - Primary */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white">
              <Sparkles className="h-8 w-8 mx-auto mb-3 text-yellow-300" />
              <h3 className="font-bold text-xl mb-2">
                Ready to Transform?
              </h3>
              <p className="text-sm text-purple-100 mb-4">
                Join a sisterhood of women committed to growth, healing, and becoming their highest selves.
              </p>
              <Button 
                onClick={handleApplyToCollective}
                className="w-full bg-white text-purple-700 hover:bg-purple-50 font-bold py-5 text-base"
              >
                Apply to The Collective
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>

            <button 
              onClick={handleClose}
              className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Maybe later
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadCapturePopup;
